import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Input,
  Button,
  Space,
  Modal,
  Table,
  Typography,
  Descriptions,
} from 'antd';
import { SearchOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PaqueryTable from '@paquery-team/lib-table';
import { useOutboundMessages } from 'services/NotificationService';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { TableDateTimeFormat } from 'constants/dateFormats';
import { usePaquers } from 'services/PaquerService';
import CreateNotificationModal from 'components/CreateNotificationModal';
import { useDebounce } from 'util/Hooks';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const Messaging = () => {
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    searchTerm: '',
    page: 1,
    limit: 20,
    excludeDeleted: true,
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // Estado local para el input de búsqueda

  // Usar el hook de debounce existente
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  // Actualizar los filtros cuando cambie el término de búsqueda con debounce
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      searchTerm: debouncedSearchTerm,
      page: 1, // Reset to first page when search changes
    }));
  }, [debouncedSearchTerm]);

  const {
    data: outboundData,
    isFetching,
    refetch,
  } = useOutboundMessages(filters);

  const { data: paquers = [] } = usePaquers();

  const handleDateChange = useCallback((dates) => {
    setFilters((prev) => ({
      ...prev,
      fromDate: dates?.[0]?.toDate() || null,
      toDate: dates?.[1]?.toDate() || null,
      page: 1, // Reset to first page when filters change
    }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value); // Solo actualizar el estado local del input
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      page: page.current,
      limit: page.pageSize,
    }));
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const clearFilters = useCallback(() => {
    setFilters({
      fromDate: null,
      toDate: null,
      searchTerm: '',
      page: 1,
      limit: 20,
      excludeDeleted: true,
    });
    setSearchInput(''); // También limpiar el input local
  }, []);

  const handleInspectMessage = useCallback((message) => {
    setSelectedMessage(message);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedMessage(null);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setCreateModalVisible(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalVisible(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    refetch(); // Recargar la lista de mensajes
  }, [refetch]);

  // Función para preparar los datos de destinatarios para la tabla del modal
  const getRecipientsData = useCallback(() => {
    if (!selectedMessage?.recipients || !paquers) return [];

    console.log({ paquers });

    return Object.entries(selectedMessage.recipients).map(
      ([recipientId, recipientInfo]) => {
        // Buscar información del paquer por ID
        const paquer = paquers.find((p) => p.id.toString() === recipientId);

        return {
          key: recipientId,
          id: recipientId,
          name: paquer
            ? `${paquer.name} ${paquer.lastName}`
            : 'N/A' + `(ID: ${recipientId})`,
          email: paquer ? paquer.email : 'N/A',
          status: recipientInfo.status === 'seen' ? 'Leído' : 'No leído',
          readDate: recipientInfo.seenTimestamp
            ? dayjs(recipientInfo.seenTimestamp).format(TableDateTimeFormat)
            : 'N/A',
          statusColor: recipientInfo.status === 'seen' ? '#52c41a' : '#faad14',
        };
      },
    );
  }, [selectedMessage, paquers]);

  const dataSource = useMemo(() => {
    if (!outboundData?.data) return [];

    return outboundData.data.map((message) => {
      const recipients = message.recipients || {};
      const totalRecipients = Object.keys(recipients).length;
      const seenRecipients = Object.values(recipients).filter(
        (recipient) => recipient.status === 'seen',
      ).length;

      return {
        key: message.id,
        id: message.id,
        title: message.title,
        createdAt: dayjs(message.creationTimestamp).format(TableDateTimeFormat),
        status: `${seenRecipients} / ${totalRecipients}`,
        statusValue: seenRecipients / totalRecipients, // For sorting
        description: message.description,
        type: message.type,
        originalMessage: message, // Guardamos el mensaje completo para el modal
      };
    });
  }, [outboundData]);

  const pagination = outboundData?.meta
    ? {
        current: outboundData.meta.page,
        pageSize: outboundData.meta.limit,
        total: outboundData.meta.total,
        pages: outboundData.meta.pages,
      }
    : DEFAULT_PAGINATE;

  const searcher = {
    key: 'searchTerm',
    onSearching: handleSearchChange,
    placeholder: 'Buscar por título...',
    allowEmptySearch: true,
  };

  const columnsSmallDevice = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const [seen, total] = status.split(' / ').map(Number);
        const percentage = total > 0 ? Math.round((seen / total) * 100) : 0;
        return `${status} (${percentage}%)`;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleInspectMessage(record.originalMessage)}
          title="Inspeccionar mensaje"
        />
      ),
    },
  ];

  const columnsMediumDevice = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Creado',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const [seen, total] = status.split(' / ').map(Number);
        const percentage = total > 0 ? Math.round((seen / total) * 100) : 0;
        return (
          <Space direction="vertical" size={0}>
            <span>{status}</span>
            <span style={{ fontSize: '12px', color: '#666' }}>
              ({percentage}%)
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleInspectMessage(record.originalMessage)}
          title="Inspeccionar mensaje"
        />
      ),
    },
  ];

  const columnsLargeDevice = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      width: '35%',
      ellipsis: true,
    },
    {
      title: 'Creado',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '25%',
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Estado (Leídos/Total)',
      dataIndex: 'status',
      key: 'status',
      width: '25%',
      sorter: (a, b) => a.statusValue - b.statusValue,
      render: (status, record) => {
        const [seen, total] = status.split(' / ').map(Number);
        const percentage = total > 0 ? Math.round((seen / total) * 100) : 0;

        return (
          <Space direction="vertical" size={0}>
            <span>{status}</span>
            <span style={{ fontSize: '12px', color: '#666' }}>
              ({percentage}%)
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleInspectMessage(record.originalMessage)}
          title="Inspeccionar mensaje"
        >
          Inspeccionar
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Filters */}
      <Card title="Filtros de búsqueda" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Rango de fechas:</strong>
            </div>
            <RangePicker
              value={[
                filters.fromDate ? dayjs(filters.fromDate) : null,
                filters.toDate ? dayjs(filters.toDate) : null,
              ]}
              onChange={handleDateChange}
              style={{ width: '100%' }}
              placeholder={['Fecha desde', 'Fecha hasta']}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Buscar:</strong>
            </div>
            <Input
              placeholder="Título del mensaje..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              onClear={() => setSearchInput('')}
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div style={{ marginBottom: '8px' }}>
              <strong>Acciones:</strong>
            </div>
            <Button onClick={clearFilters}>Limpiar filtros</Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Mensajes Salientes
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateModal}
            >
              Crear Notificación
            </Button>
          </Col>
        </Row>

        <PaqueryTable
          loading={isFetching}
          header={{
            searcher,
            refresh: handleRefresh,
          }}
          dataSource={dataSource}
          paginate={pagination}
          onChangePaginate={handlePageChange}
          dataColumns={columnsLargeDevice}
          colsForMediumDevice={columnsMediumDevice}
          colsForSmallDevice={columnsSmallDevice}
          colsForLargeDevice={columnsLargeDevice}
        />
      </Card>

      {/* Modal de inspección de mensaje */}
      <Modal
        title="Inspeccionar Mensaje"
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Cerrar
          </Button>,
        ]}
        width={1200}
        styles={{
          body: { maxHeight: '70vh', overflowY: 'auto' },
        }}
      >
        {selectedMessage && (
          <Row gutter={[24, 16]}>
            {/* Información del mensaje - Izquierda */}
            <Col xs={24} lg={12}>
              <Card title="Información del Mensaje" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Título">
                    <Text strong>{selectedMessage.title}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Descripción">
                    {selectedMessage.description || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tipo">
                    {selectedMessage.type}
                  </Descriptions.Item>
                  <Descriptions.Item label="Fecha de creación">
                    {dayjs(selectedMessage.creationTimestamp).format(
                      TableDateTimeFormat,
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Última actualización">
                    {dayjs(selectedMessage.updatedAt).format(
                      TableDateTimeFormat,
                    )}
                  </Descriptions.Item>
                  {selectedMessage.url && (
                    <Descriptions.Item label="URL">
                      <Text code>{selectedMessage.url}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedMessage.scope && (
                    <Descriptions.Item label="Alcance">
                      {selectedMessage.scope}
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {/* Información de imágenes si existe */}
                {selectedMessage.images && (
                  <div style={{ marginTop: 16 }}>
                    <Title level={5}>Imágenes</Title>
                    {selectedMessage.images.main && (
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>Principal: </Text>
                        <Text code>{selectedMessage.images.main.src}</Text>
                      </div>
                    )}
                    {selectedMessage.images.left &&
                      selectedMessage.images.left.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>Izquierda: </Text>
                          {selectedMessage.images.left.map((img, idx) => (
                            <div key={idx}>
                              <Text code>{img.src}</Text>
                            </div>
                          ))}
                        </div>
                      )}
                    {selectedMessage.images.right &&
                      selectedMessage.images.right.length > 0 && (
                        <div>
                          <Text strong>Derecha: </Text>
                          {selectedMessage.images.right.map((img, idx) => (
                            <div key={idx}>
                              <Text code>{img.src}</Text>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                )}

                {/* Información de botones si existe */}
                {selectedMessage.buttons &&
                  selectedMessage.buttons.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Title level={5}>Botones</Title>
                      {selectedMessage.buttons.map((button, idx) => (
                        <div key={idx} style={{ marginBottom: 8 }}>
                          <Text strong>{button.title}</Text> -{' '}
                          <Text type="secondary">{button.type}</Text>
                          {button.action?.type && (
                            <div style={{ marginLeft: 16 }}>
                              <Text>Acción: {button.action.type}</Text>
                              {button.action.objectiveId && (
                                <Text> - ID: {button.action.objectiveId}</Text>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
              </Card>
            </Col>

            {/* Tabla de destinatarios - Derecha */}
            <Col xs={24} lg={12}>
              <Card title="Destinatarios" size="small">
                <Table
                  dataSource={getRecipientsData()}
                  size="small"
                  pagination={{ pageSize: 10 }}
                  scroll={{ y: 400 }}
                  columns={[
                    {
                      title: 'Nombre',
                      dataIndex: 'name',
                      key: 'name',
                      ellipsis: true,
                    },
                    {
                      title: 'Email',
                      dataIndex: 'email',
                      key: 'email',
                      ellipsis: true,
                    },
                    {
                      title: 'Estado',
                      dataIndex: 'status',
                      key: 'status',
                      render: (status, record) => (
                        <span
                          style={{
                            color: record.statusColor,
                            fontWeight: 'bold',
                          }}
                        >
                          {status}
                        </span>
                      ),
                    },
                    {
                      title: 'Fecha de lectura',
                      dataIndex: 'readDate',
                      key: 'readDate',
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Modal>

      {/* Modal de creación de notificación */}
      <CreateNotificationModal
        visible={createModalVisible}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Messaging;
